import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { 
  fetchShoppingList,
  fetchProducts, 
  fetchProductGroups,
  fetchQuantityUnits,
  fetchQuantityUnitConversions,
  fetchCurrentStock,
  fetchLocations,
  fetchProductBarcodes,
  fetchUserSettings,
  fetchRecipes,
  fetchRecipeIngredients
} from "utils/grocyApi.ts";
import { queryKeys } from "./queryKeys.ts";
import { getOpenAIKey } from "utils/storage.ts";

// Single file containing all base query hooks
export const useProductsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.products,
    queryFn: fetchProducts,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useLocationsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.locations,
    queryFn: fetchLocations,
    staleTime: 5 * 60 * 1000,
  });
};

export const useQuantityUnitsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.quantityUnits,
    queryFn: fetchQuantityUnits,
    staleTime: 5 * 60 * 1000,
  });
};

export const useStockQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.stock,
    queryFn: fetchCurrentStock,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
  });
};

export const useShoppingListQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.shoppingList,
    queryFn: fetchShoppingList,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useProductGroupsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.productGroups,
    queryFn: fetchProductGroups,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useProductBarcodesQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.productBarcodes,
    queryFn: fetchProductBarcodes,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUserSettingsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.userSettings,
    queryFn: fetchUserSettings,
    staleTime: 10 * 60 * 1000, // 10 minutes - settings don't change often
  });
};

export const useRecipesQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.recipes,
    queryFn: fetchRecipes,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRecipeIngredientsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.recipeIngredients,
    queryFn: fetchRecipeIngredients,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}; 

export const useQuantityUnitConversionsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.quantityUnitConversions,
    queryFn: fetchQuantityUnitConversions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}; 

// AI model queries
const OPENAI_MODELS_URL = "https://api.openai.com/v1/models";
const OPENROUTER_MODELS_URL = "https://openrouter.ai/api/v1/models";

export const useOpenAIModelsQuery = (apiKey?: string | null) => {
  // If apiKey not provided, try from storage for convenience in UI contexts
  const resolvedKey = apiKey ?? getOpenAIKey();
  return useQuery({
    queryKey: [...queryKeys.aiModelsOpenAI, resolvedKey ?? "no-key"],
    queryFn: async () => {
      if (!resolvedKey) throw new Error("OpenAI key required");
      const res = await fetch(OPENAI_MODELS_URL, {
        headers: { Authorization: `Bearer ${resolvedKey}` },
      });
      if (!res.ok) throw new Error(`OpenAI models failed: ${res.status}`);
      return res.json();
    },
    enabled: !!resolvedKey,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};

export const useOpenRouterModelsQuery = () => {
  return useSuspenseQuery({
    queryKey: queryKeys.aiModelsOpenRouter,
    queryFn: async () => {
      const res = await fetch(OPENROUTER_MODELS_URL);
      if (!res.ok) throw new Error(`OpenRouter models failed: ${res.status}`);
      return res.json();
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
};